/**
 * Object fit modes for image layout
 *
 * CSS-compatible object-fit implementation for image sizing and positioning
 */

import { Point, point, Rect, rect, Size, size } from '@geom';

/**
 * Object fit modes (CSS-compatible)
 */
export enum ObjectFit {
  FILL = 'fill', // Stretch to fill container
  CONTAIN = 'contain', // Scale to fit inside, preserve aspect
  COVER = 'cover', // Scale to cover container, preserve aspect
  SCALE_DOWN = 'scale-down', // Like contain but never scale up
  NONE = 'none' // Use intrinsic size
}

/**
 * Result of object fit calculation
 */
export interface ObjectFitLayout {
  /** Source rectangle (what part of image to show) */
  srcRect: Rect;
  /** Destination rectangle (where to draw in container) */
  destRect: Rect;
}

/**
 * Calculate object fit layout
 */
export function calculateObjectFitLayout(
  sourceSize: Size,
  containerSize: Size,
  objectFit: ObjectFit,
  objectPosition?: Point
): ObjectFitLayout {
  const containerRect = rect({
    center: point(containerSize.width / 2, containerSize.height / 2),
    size: containerSize
  });
  const pos = objectPosition ?? point(0.5, 0.5); // Default to center

  switch (objectFit) {
    case ObjectFit.FILL:
      return {
        srcRect: rect(0, 0, sourceSize.width, sourceSize.height),
        destRect: containerRect
      };

    case ObjectFit.CONTAIN: {
      const scale = Math.min(
        containerSize.width / sourceSize.width,
        containerSize.height / sourceSize.height
      );
      const scaledSize = sourceSize.mul(scale);
      const offset = point(
        (containerSize.width - scaledSize.width) * pos.x,
        (containerSize.height - scaledSize.height) * pos.y
      );

      return {
        srcRect: rect(0, 0, sourceSize.width, sourceSize.height),
        destRect: rect(
          offset.x,
          offset.y,
          offset.x + scaledSize.width,
          offset.y + scaledSize.height
        )
      };
    }

    case ObjectFit.COVER: {
      const scale = Math.max(
        containerSize.width / sourceSize.width,
        containerSize.height / sourceSize.height
      );

      const srcSize = size(containerSize.width / scale, containerSize.height / scale);
      const srcOffset = point(
        (sourceSize.width - srcSize.width) * pos.x,
        (sourceSize.height - srcSize.height) * pos.y
      );

      return {
        srcRect: rect(
          srcOffset.x,
          srcOffset.y,
          srcOffset.x + srcSize.width,
          srcOffset.y + srcSize.height
        ),
        destRect: containerRect
      };
    }

    case ObjectFit.SCALE_DOWN: {
      const scale = Math.min(
        1.0, // Never scale up
        containerSize.width / sourceSize.width,
        containerSize.height / sourceSize.height
      );
      const scaledSize = sourceSize.mul(scale);
      const offset = point(
        (containerSize.width - scaledSize.width) * pos.x,
        (containerSize.height - scaledSize.height) * pos.y
      );

      return {
        srcRect: rect(0, 0, sourceSize.width, sourceSize.height),
        destRect: rect(
          offset.x,
          offset.y,
          offset.x + scaledSize.width,
          offset.y + scaledSize.height
        )
      };
    }

    case ObjectFit.NONE: {
      const offset = point(
        (containerSize.width - sourceSize.width) * pos.x,
        (containerSize.height - sourceSize.height) * pos.y
      );

      return {
        srcRect: rect(0, 0, sourceSize.width, sourceSize.height),
        destRect: rect(
          offset.x,
          offset.y,
          offset.x + sourceSize.width,
          offset.y + sourceSize.height
        )
      };
    }
  }
}
